import React, { useEffect, useCallback, useRef } from "react";
import { Accordion, Button, Card, Col, Form, Row } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router";
// general page layout
import NavigationBar from "../navigation/NavigationBar";
import SidebarClinician from "../sidebars/SidebarClinician";
import Footer from "../Footer";
//helpers
import { useAlertHelpers } from "../../helper/Hooks/useAlertHelpers";
import { appendScript } from "../../helper/appendScript";
import { removeScript } from "../../helper/removeScript";
import { toErrorType } from "../../helper/alertBoxes";
import { usePartialState } from "../../helper/Hooks/usePartialState";
// others
import { getClinicianReport } from "../../services/getFunctions";
import { LocationState } from "../../types/routeTypes";
import { ReportEntry } from "../../types/reportTypes";
import { MdViewList, MdLocalPrintshop } from "react-icons/md";
import {
  getGermanTodayDisplay,
  formatCalendarDate,
  formatDateTime,
} from "../../helper/dateHelpers";

interface ClinicianReportProps {
  title: string;
}

/**Clinician reports component. */
interface ClinicianReportState {
  user_type: string;
  clinic_id: string;
  clinic_name: string;
  username: string;
  survey_session_id: string;
  patient_code: string;
  birthday: string;
  gender: string;
  stw_type: string;
  started_time: string;
  update_time: string;
  report: string;
  patientReport: boolean;
  patientReports: ReportEntry[];
  today: string;
  accordionMyClinics: string;
  accordionMyPatients: string;
  selectedClinicId: string;
}

const ClinicianReport = ({ title }: ClinicianReportProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const scriptLoadedRef = useRef(false); //aviod duplicate script loading
  const isMountedRef = useRef(true); //avoid incorrect state updates

  const locationState = location.state as LocationState;
  const locationStateRef = useRef<LocationState | null>(locationState);
  if (locationState) {
    locationStateRef.current = locationState; // update only when state exists
  }

  const initialState: ClinicianReportState = {
    user_type: "clinician",
    clinic_id: "",
    selectedClinicId: "",
    clinic_name: "",
    username: "",
    survey_session_id: "",
    patient_code: "",
    birthday: "",
    gender: "",
    stw_type: "",
    started_time: "",
    update_time: "",
    report: "",
    patientReport: false,
    patientReports: [],
    today: getGermanTodayDisplay(),
    accordionMyClinics: "-1",
    accordionMyPatients: "-1",
  };

  const [state, updateState] = usePartialState(initialState);
  const {
    reportGetClinicianReportsError,
    reportGetSurveySessionReportError,
    showNonConsentModal,
  } = useAlertHelpers();

  /**Extract scripts in html-response.
   * Run clean javascript-code.
   */
  const extractScript = useCallback((reportContent: string) => {
    const scriptMatch = /<script\s*.*>[\s\S]*<\/script>/gi.exec(reportContent);

    if (scriptMatch) {
      const [extract] = scriptMatch;
      const runScript = extract;

      if (runScript !== undefined) {
        const updateScript = runScript.replace(
          /<\/script>([\s\S]*?)<script[^>]*>/gm,
          "",
        );
        const cleanScript = updateScript.replace(
          /<script[^>]*>([\s\S]*?)<\/script>/gm,
          "$1",
        );

        setTimeout(() => {
          // eslint-disable-next-line
          window.eval(cleanScript);
        }, 100);
      }
    }
  }, []);

  /**Open patients report. */
  const openPatientReport = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();

      navigate("/clinician/patient-report", {
        state: {
          clinic_id: state.selectedClinicId,
          clinic_name: state.clinic_name,
          username: state.username,
          survey_session_id: state.survey_session_id,
          patient_code: state.patient_code,
          birthday: state.birthday,
          gender: state.gender,
          stw_type: state.stw_type,
          started_time: state.started_time,
          update_time: state.update_time,
          user_type: state.user_type,
        },
      });
    },
    [state, navigate],
  );

  /**Method for loading the clinician report from backend.
   * The final_report script would be appended to the component after loading the report from backend.
   */
  useEffect(() => {
    // avoid updating state after components has unmounted
    let mounted = true;

    const initializeAndLoadReport = async () => {
      try {
        if (title) {
          document.title = title;
        }

        window.scrollTo(0, 0);

        if (!locationStateRef.current) {
          console.warn("Location state is missing");
          return;
        }

        const formattedStartTime = formatDateTime(
          locationStateRef.current.started_time,
        );
        const formattedUpdateTime = formatDateTime(
          locationStateRef.current.update_time,
        );
        const formattedBirthday = formatCalendarDate(
          locationStateRef.current.birthday,
        );

        const {
          clinic_id = "",
          clinic_name = "",
          username = "",
          survey_session_id = "",
          patient_code = "",
          gender = "",
          stw_type = "",
        } = locationStateRef.current;

        if (mounted) {
          updateState({
            clinic_id,
            clinic_name,
            username,
            survey_session_id,
            patient_code,
            birthday: formattedBirthday,
            gender,
            stw_type,
            started_time: formattedStartTime,
            update_time: formattedUpdateTime,
            selectedClinicId: clinic_id,
          });
        }

        //check state existence
        if (!survey_session_id) {
          throw new Error("Survery session id is required.");
        }

        const report = await getClinicianReport(Number(survey_session_id));
        if (!report) {
          showNonConsentModal();
          reportGetClinicianReportsError(
            new Error("Clinician-reports are empty"),
          );
        }

        if (mounted) {
          updateState({ report });

          if (!scriptLoadedRef.current) {
            scriptLoadedRef.current = true;
            try {
              const scriptPath = `${process.env.PUBLIC_URL}/js/final_report.js`;
              await appendScript(scriptPath);
            } catch (error) {
              console.error("Error in loading report:", error);
              scriptLoadedRef.current = false;
            }
          } else {
            extractScript(report);
          }
        }
      } catch (error) {
        if (mounted) {
          console.error("Error in initializeAndLoadReport:", error);

          // different types of errors
          if (error instanceof Error) {
            if (error.message.includes("Clinician-reports are empty")) {
              reportGetClinicianReportsError(toErrorType(error));
            } else {
              reportGetSurveySessionReportError(toErrorType(error));
            }
          } else {
            reportGetSurveySessionReportError(toErrorType(error));
          }
        }
      }
    };

    initializeAndLoadReport();

    return () => {
      mounted = false;
    };
  }, [
    title,
    extractScript,
    updateState,
    showNonConsentModal,
    reportGetClinicianReportsError,
    reportGetSurveySessionReportError,
  ]);

  // to ensure extractScript only runs after the DOM is updated
  useEffect(() => {
    if (!state.report) return;
    extractScript(state.report);
  }, [state.report, extractScript]);

  // clean function, remove script when component unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      const scriptPath = `${process.env.PUBLIC_URL}/js/final_report.js`;
      removeScript(scriptPath);
    };
  }, [state.report, extractScript]);

  /**
   * This function handles the scroll effect manually instead of triggering React Router
   * The single page navigation is not allowed by clicking Seitenuebersicht
   */
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a[href^="#"]');
      if (!anchor) return;

      e.preventDefault();
      const id = anchor.getAttribute("href")?.slice(1);
      document.getElementById(id ?? "")?.scrollIntoView({ behavior: "smooth" });
    };

    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);

  return (
    <div className="header-fixed" id="body">
      {/* navigation bar (bar on the top) */}
      <NavigationBar />
      <div className="container-fluid">
        <div className="row">
          {/* Sidebar (bar on the side) */}
          <SidebarClinician />
          <div
            className="col container p-3 ergebnisbericht_container"
            id="final-report"
          >
            <Col id="report-heading" lg="12">
              <h1>Ergebnisbericht</h1>
            </Col>
            {/* general information about survey report */}
            <Card
              tabIndex={0}
              id="report-head-info"
              className="card-default card-report-custom widget-media p-4 active border"
            >
              <Card.Body className="m-0 p-0">
                <Row>
                  <Col className="col-12 col-sm-6 col-print-6">
                    <Row>
                      <Col
                        md="3"
                        className="d-flex justify-content-center not_printable"
                      >
                        <div
                          className="d-flex justify-content-center align-items-center rounded-circle border-indigo-dye border-2"
                          style={{
                            width: "50px",
                            height: "50px",
                          }}
                        >
                          <span className="material-symbols-rounded indigo-dye">
                            3p
                          </span>
                        </div>
                      </Col>
                      <Col md="9">
                        <Row className="profile-data profile-data-report">
                          <Col md={5}>SysDok-ID:</Col>
                          <Col md={5} className="text-grey">
                            {state.patient_code}
                          </Col>
                        </Row>
                        <hr className="separator text-grey" />
                        <Row className="profile-data profile-data-report">
                          <Col md={5}>Geburtsdatum:</Col>
                          <Col md={5} className="text-grey">
                            {state.birthday}
                          </Col>
                        </Row>
                        <hr className="separator text-grey" />
                        <Row className="profile-data profile-data-report">
                          <Col md={5}>Geschlecht:</Col>
                          <Col md={5} className="text-grey">
                            {state.gender === "MALE"
                              ? "Männlich"
                              : state.gender === "FEMALE"
                                ? "Weiblich"
                                : state.gender === "DIVERSE"
                                  ? "Divers"
                                  : state.gender}
                          </Col>
                        </Row>
                      </Col>
                    </Row>
                  </Col>
                  <Col className="col-12 col-sm-6 col-print-6">
                    <Row>
                      <Col
                        md="3"
                        className="d-flex justify-content-center not_printable"
                      >
                        <div
                          className="d-flex justify-content-center align-items-center rounded-circle border-indigo-dye border-2"
                          style={{
                            width: "50px",
                            height: "50px",
                          }}
                        >
                          <span className="material-symbols-rounded indigo-dye">
                            timeline
                          </span>
                        </div>
                      </Col>
                      <Col md="9">
                        <Row className="profile-data profile-data-report">
                          <Col md={5}>Messzeitpunkt:</Col>
                          <Col md={5} className="text-grey">
                            {(() => {
                              switch (state.stw_type) {
                                case "PRE":
                                  return "Aufnahme";
                                case "INTERMEDIATE":
                                  return "Zwischenbefragung";
                                case "POST":
                                  return "Entlassung";
                                case "FOLLOW_UP_1":
                                  return "Nachsorge 6 Monate";
                                case "FOLLOW_UP_2":
                                  return "Nachsorge 12 Monate";
                                case "FOLLOW_UP_3":
                                  return "Nachsorge 18 Monate";
                                default:
                                  return null;
                              }
                            })()}
                          </Col>
                        </Row>
                        <hr className="separator text-grey" />
                        <Row className="profile-data profile-data-report">
                          <Col md={5}>Screeningbeginn:</Col>
                          <Col md={5} className="text-grey">
                            {state.started_time ?? "Kein Datum vorhanden"}
                          </Col>
                        </Row>
                        <Row className="profile-data profile-data-report">
                          <Col md={5}>Letztes Update des Screenings:</Col>
                          <Col md={5} className="text-grey">
                            {state.update_time ?? "Kein Datum vorhanden"}
                          </Col>
                        </Row>
                        <hr className="separator text-grey" />
                        <Row className="profile-data profile-data-report">
                          <Col md={5}>Abrufdatum:</Col>
                          <Col md={5} className="text-grey">
                            {state.today}
                          </Col>
                        </Row>
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            <Row>
              {/* TOC */}
              <Col>
                <Accordion
                  defaultActiveKey="0"
                  className="accordion my-2"
                  id="report_overview_box"
                >
                  <Accordion.Item eventKey="0">
                    <Accordion.Header>
                      <MdViewList className="me-1" /> Seitenübersicht
                    </Accordion.Header>
                    <Accordion.Body className="pb-0 m-2">
                      <ul className="list-unstyled" id="final_report_overview">
                        {[
                          { id: "dep", label: "Depression" },
                          { id: "anx", label: "Angst" },
                          { id: "exis", label: "Exis" },
                          { id: "evos", label: "Evos" },
                          { id: "eri", label: "ERI" },
                          { id: "wai", label: "WAI" },
                          { id: "socio", label: "Socio" },
                          { id: "eq5d", label: "EQ5D" },
                        ].map((item) => (
                          <li key={item.id}>
                            <a
                              href={`#${item.id}_clinician_container`}
                              className="text-decoration-none space-cadet"
                            >
                              <div className="media-body d-flex align-items-center">
                                {/* icon for how serious the dimension result is */}
                                <div
                                  className={`position-relative me-2 list_item_${item.id}_status`}
                                  id={`list_item_${item.id}_status`}
                                />
                                {/* name/ label of dimension */}
                                <span className="">
                                  Screening Ergebnis: {item.label}
                                </span>
                              </div>
                            </a>
                          </li>
                        ))}
                        {/* more dimensions */}
                        <li
                          id="list_item_management_container"
                          style={{ display: "none" }}
                        >
                          <a
                            href="#management_container"
                            className="media media-message eb-navigation"
                          >
                            <div className="media-body d-flex">
                              <h4 className="title">
                                Weitere Gesundheitsdimensionen
                              </h4>
                            </div>
                          </a>
                        </li>
                        {/* Documentation aid */}
                        <li
                          id="list_item_entlassbericht_container"
                          style={{ display: "none" }}
                        >
                          <a
                            href="#entlassbericht_container"
                            className="text-decoration-none space-cadet"
                          >
                            <div className="media-body d-flex">
                              <span className="title">Dokumentationshilfe</span>
                            </div>
                          </a>
                        </li>
                      </ul>

                      {state.patientReport ? (
                        <div className="card-footer">
                          <div className="d-grid gap-2 d-md-flex justify-content-md-end">
                            <Button
                              variant="primary"
                              onClick={openPatientReport}
                            >
                              <span className="material-symbols-rounded me-1">
                                assignment_ind
                              </span>
                              Bericht für Patient*innen einsehen
                            </Button>
                          </div>
                        </div>
                      ) : null}
                    </Accordion.Body>
                  </Accordion.Item>
                </Accordion>
              </Col>
              {/* printing options */}
              <Col>
                <Accordion
                  defaultActiveKey="1"
                  className="card-default accordion my-2"
                  id="report_printview_box"
                >
                  <Accordion.Item eventKey="1">
                    <Accordion.Header id="heading_report_printview_box">
                      <MdLocalPrintshop className="me-1" />
                      Druckoptionen
                    </Accordion.Header>
                    <Accordion.Body className="pb-0 pt-3">
                      <Row id="final_report_printview">
                        <Col
                          className="col-12 my-2"
                          id="print_toggle_core_container"
                        >
                          <Row>
                            <Col md="9">
                              <h6 className="text-dark">Depression & Angst</h6>
                              {/* <p className="text-grey">
                                  Screening Ergebnisse
                                </p> */}
                            </Col>
                          </Row>
                          <Row className="report-switch-row">
                            <Col md="9">
                              <p className="text-grey text-print-sub">
                                Screening Ergebnisse
                              </p>
                            </Col>
                            <Col md="3">
                              <Form.Label className="switch switch-icon switch-primary switch-pill form-control-label">
                                <div className="form-check form-switch">
                                  <input
                                    type="checkbox"
                                    role="switch"
                                    id="print_toggle_detailinfo_btn"
                                    aria-label="Screening Ergebnisse anzeigen"
                                    value="on"
                                    className="form-check-input disabled"
                                    style={{ transform: "scale(2)" }}
                                    checked
                                    disabled
                                  />
                                </div>
                              </Form.Label>
                            </Col>
                          </Row>
                        </Col>
                        <Col
                          className="col-12 my-2"
                          id="print_toggle_detailinfo_container"
                        >
                          <Row className="report-switch-row" tabIndex={0}>
                            <Col md="9">
                              {/* <h6 className="text-dark">
                                  Detailinformationen
                                </h6> */}
                              <p className="text-grey text-print-sub">
                                Antworten im Detail
                              </p>
                            </Col>
                            <Col md="3">
                              <Form.Label className="switch switch-icon switch-primary switch-pill form-control-label">
                                <div className="form-check form-switch">
                                  <input
                                    type="checkbox"
                                    role="switch"
                                    id="print_detail"
                                    aria-label="Antworten im Detail anzeigen"
                                    value="off"
                                    className="form-check-input"
                                    style={{ transform: "scale(2)" }}
                                  />
                                </div>
                              </Form.Label>
                            </Col>
                          </Row>
                        </Col>

                        <Col
                          className="col-12 my-2"
                          id="print_toggle_management_container"
                          style={{ display: "none" }}
                        >
                          <Row>
                            <Col md="9">
                              <h6 className="text-dark">
                                Weitere Gesundheitsdimensionen
                              </h6>
                            </Col>
                            <Col md="3">
                              <Form.Label className="switch switch-icon switch-primary switch-pill form-control-label">
                                <div className="form-check form-switch">
                                  <input
                                    type="checkbox"
                                    role="switch"
                                    id="print_management"
                                    aria-label="Weitere Gesundheitsdimensionen anzeigen"
                                    value="off"
                                    className="form-check-input"
                                    style={{ transform: "scale(2)" }}
                                  />
                                </div>
                              </Form.Label>
                            </Col>
                          </Row>
                        </Col>
                        <Col
                          className="col-12 my-2"
                          id="print_toggle_entlassbericht_container"
                          style={{ display: "none" }}
                        >
                          <Row>
                            <Col md="9">
                              <h6 className="text-dark">Dokumentationshilfe</h6>
                            </Col>
                            <Col md="3">
                              <Form.Label className="switch switch-icon switch-primary switch-pill form-control-label">
                                <div className="form-check form-switch">
                                  <input
                                    type="checkbox"
                                    role="switch"
                                    id="print_entlassbericht_toggle"
                                    aria-label="Dokumentationshilfe anzeigen"
                                    value="off"
                                    className="form-check-input"
                                    style={{ transform: "scale(2)" }}
                                  />
                                </div>
                              </Form.Label>
                            </Col>
                          </Row>
                        </Col>
                      </Row>
                      <div className="card-footer d-flex justify-content-end">
                        {/* id adds function in final_report.js */}
                        <Button
                          className="m-2"
                          variant="primary"
                          id="print_final_report"
                        >
                          <MdLocalPrintshop /> Drucken
                        </Button>
                      </div>
                    </Accordion.Body>
                  </Accordion.Item>
                </Accordion>
              </Col>
            </Row>
            {/* === generated report html start === */}
            {/* TODO: refactor html of report to be generated in client instead of server */}
            <div
              id="final-html"
              dangerouslySetInnerHTML={{ __html: state.report }}
            />

            {/* === generated report html end === */}

            {/* more dimensions */}
            <Row className="mt-3" id="management_container">
              <Col lg="12">
                <Accordion
                  className="accordion print-keep-together"
                  id="management_clinician"
                >
                  <Accordion.Item eventKey="1">
                    <Accordion.Header id="heading_management_clinician">
                      Weitere Gesundheitsdimensionen
                    </Accordion.Header>
                    <Accordion.Body id="management_content_wrapper" />
                  </Accordion.Item>
                </Accordion>
              </Col>
            </Row>
            {/* documentation aid */}
            <Row className="entlassbericht mt-3" id="entlassbericht_container">
              <Col lg="12">
                <Accordion id="entlassbericht_clinician" className="accordion">
                  <Accordion.Item eventKey="1">
                    <Accordion.Header id="heading_entlassbericht_clinician">
                      Dokumentationshilfe
                    </Accordion.Header>
                    <Accordion.Body id="entlassbericht_content_wrapper" />
                    <div className="card-footer p-2 not_printable">
                      <div className="d-grip gap-2 d-md-flex justify-content-md-end">
                        <Button variant="primary" id="print_entlassbericht">
                          <MdLocalPrintshop /> Dokumentationshilfe drucken
                        </Button>
                        <Button
                          variant="outline-secondary"
                          id="copy_entlassbericht"
                        >
                          In Zwischenablage kopieren
                        </Button>
                      </div>
                    </div>
                  </Accordion.Item>
                </Accordion>
              </Col>
            </Row>
            {/* END html end*/}
          </div>
        </div>
        <Footer>
          {state.clinic_id}
          {state.clinic_name}
          {state.username}
          {state.user_type}
        </Footer>
      </div>
    </div>
  );
};

export default ClinicianReport;
